import React, { useState } from "react";
import { ChevronDown, HelpCircle, ShieldCheck, Clock, DollarSign, Sparkles, Users, Phone, Mail, MessageSquare, MapPin, CheckCircle, Truck, Leaf, Home, Building, Award } from "lucide-react";
import { Link } from "react-router-dom";

const FAQSection = () => {
  const [activeCategory, setActiveCategory] = useState("general");
  const [openFAQs, setOpenFAQs] = useState([0, 4, 8]);

  const toggleFAQ = (index) => {
    if (openFAQs.includes(index)) {
      setOpenFAQs(openFAQs.filter(i => i !== index));
    } else {
      setOpenFAQs([...openFAQs, index]);
    }
  };

  const categories = [
    { id: "general", label: "General", icon: <HelpCircle size={18} />, count: 8 },
    { id: "residential", label: "Residential", icon: <Home size={18} />, count: 6 },
    { id: "commercial", label: "Commercial", icon: <Building size={18} />, count: 5 },
    { id: "pricing", label: "Pricing", icon: <DollarSign size={18} />, count: 6 },
    { id: "booking", label: "Booking", icon: <Clock size={18} />, count: 5 },
    { id: "safety", label: "Safety", icon: <ShieldCheck size={18} />, count: 5 },
    { id: "specialized", label: "Specialized", icon: <Award size={18} />, count: 5 },
  ];

  const allFAQs = {
    general: [
      {
        question: "What areas do Knight Cleanings service in Melbourne VIC?",
        answer: "Knight Cleanings provides professional cleaning services across the entire Melbourne metropolitan area including CBD, Dandenong, Hallam, Hampton Park, Clayton, Keysborough, Glen Waverley, Mount Waverley, Noble Park, Lynbrook, Doveton, Mornington, Rye, Rosebud, Frankston, and all surrounding suburbs. We offer comprehensive cleaning solutions for residential, commercial, and emergency cleaning needs throughout Victoria.",
        keywords: ["Melbourne cleaning services", "professional cleaning Melbourne", "cleaning services Victoria"]
      },
      {
        question: "How long has Knight Cleanings been providing cleaning services?",
        answer: "Knight Cleanings has been serving the Melbourne community with professional cleaning solutions for over 15 years. With extensive experience in both residential and commercial cleaning, we've built a reputation for reliability, quality service, and customer satisfaction. Our team of trained professionals uses state-of-the-art equipment and eco-friendly cleaning products.",
        keywords: ["experienced cleaners Melbourne", "professional cleaning company Melbourne", "established cleaning service"]
      },
      {
        question: "What makes Knight Cleanings different from other Melbourne cleaning companies?",
        answer: "Knight Cleanings stands out with our comprehensive approach: We use eco-friendly cleaning products, provide fully insured and bonded services, offer 24/7 emergency cleaning, and have a 100% satisfaction guarantee. Our team undergoes continuous training, and we specialize in everything from regular house cleaning to end of lease cleaning, carpet cleaning, and emergency restoration services.",
        keywords: ["best cleaning service Melbourne", "premium cleaning solutions", "reliable cleaners Melbourne"]
      },
      {
        question: "Are your Melbourne cleaners background checked and trained?",
        answer: "Yes, every Knight Cleanings team member undergoes comprehensive background checks, police clearance verification, and extensive cleaning training. Our cleaners are trained in modern cleaning techniques, safety protocols, and customer service. We ensure all staff are professional, reliable, and committed to delivering exceptional cleaning results for Melbourne homes and businesses.",
        keywords: ["trusted cleaners Melbourne", "professional cleaning team", "background checked cleaners"]
      },
      {
        question: "What is your cancellation and rescheduling policy for cleaning services?",
        answer: "We require 24 hours notice for cancellations or rescheduling. Cancellations made with at least 24 hours notice incur no charge. For cancellations within 24 hours, a $50 fee may apply, except in cases of genuine emergencies. We understand plans can change and will work with you to reschedule at a convenient time for both parties.",
        keywords: ["cleaning cancellation policy", "rescheduling cleaning service", "flexible cleaning appointments"]
      },
      {
        question: "Do I need to be home during cleaning service in Melbourne?",
        answer: "No, you don't need to be present during our cleaning service. Many of our Melbourne clients provide us with secure access methods such as keys, lockboxes, or access codes. We follow strict security protocols and are fully insured for your peace of mind. We can clean while you're at work, running errands, or away from home.",
        keywords: ["key holder cleaning service", "unattended cleaning", "secure cleaning service Melbourne"]
      },
      {
        question: "What cleaning equipment and products do you use in Melbourne homes?",
        answer: "We use professional-grade, eco-friendly cleaning products that are safe for children, pets, and the environment. Our equipment includes commercial-grade vacuum cleaners with HEPA filters, steam cleaners for deep sanitization, microfiber technology for superior cleaning, and truck-mounted systems for carpet cleaning. All products are biodegradable and non-toxic.",
        keywords: ["eco-friendly cleaning Melbourne", "professional cleaning equipment", "safe cleaning products Melbourne"]
      },
      {
        question: "Do you offer cleaning services for special events or occasions in Melbourne?",
        answer: "Yes, we provide specialized cleaning services for weddings, parties, corporate events, and special occasions throughout Melbourne. Our pre-event and post-event cleaning services ensure your venue is spotless. We can work around your event schedule and provide same-day service when needed for last-minute preparations.",
        keywords: ["event cleaning Melbourne", "party cleaning service", "special occasion cleaning"]
      }
    ],
    residential: [
      {
        question: "What is included in your regular house cleaning service in Melbourne?",
        answer: "Our regular Melbourne house cleaning includes: dusting all surfaces, vacuuming and mopping floors, kitchen cleaning (appliances, countertops, sinks), bathroom sanitization (toilets, showers, mirrors), bedroom cleaning, living area tidying, and trash removal. We offer weekly, bi-weekly, or monthly schedules to maintain your home's cleanliness.",
        keywords: ["house cleaning Melbourne", "regular cleaning service", "home cleaning checklist"]
      },
      {
        question: "How does end of lease cleaning work for Melbourne rental properties?",
        answer: "Our end of lease cleaning service in Melbourne is designed to help tenants get their full bond back. We perform a comprehensive clean including: carpet steam cleaning, window washing, wall cleaning, appliance deep cleaning, cabinet interior cleaning, light fixtures, and all hard-to-reach areas. We follow real estate agency checklists and provide a satisfaction guarantee.",
        keywords: ["end of lease cleaning Melbourne", "bond cleaning service", "move out cleaning Melbourne"]
      },
      {
        question: "Can you clean carpets and rugs in Melbourne homes?",
        answer: "Yes, we specialize in professional carpet and rug cleaning services for Melbourne homes. Using advanced steam cleaning and dry cleaning methods, we remove deep-seated dirt, stains, and allergens. We treat all fabric types including wool, synthetic fibers, and delicate oriental rugs. Our service includes pre-treatment, deep cleaning, and protective coating application.",
        keywords: ["carpet cleaning Melbourne", "rug cleaning service", "steam cleaning Melbourne"]
      },
      {
        question: "Do you offer mattress and upholstery cleaning in Melbourne?",
        answer: "Absolutely. Our Melbourne mattress cleaning service removes allergens, dust mites, stains, and odors using hospital-grade sanitization methods. For upholstery, we clean sofas, chairs, curtains, and fabric surfaces with specialized equipment that protects fabric integrity while providing deep cleaning and stain removal.",
        keywords: ["mattress cleaning Melbourne", "upholstery cleaning service", "fabric cleaning Melbourne"]
      },
      {
        question: "What is included in your deep cleaning service for Melbourne homes?",
        answer: "Our Melbourne deep cleaning service includes: inside appliances (oven, refrigerator), window tracks and sills, baseboards, light fixtures, ceiling fans, cabinet interiors, door frames, switch plates, and hard-to-reach areas. This intensive service is perfect for seasonal cleaning, move-in preparations, or giving your home a thorough refresh.",
        keywords: ["deep cleaning Melbourne", "spring cleaning service", "intensive house cleaning"]
      },
      {
        question: "How do you handle curtain and blind cleaning in Melbourne properties?",
        answer: "We offer both on-site and off-site curtain cleaning services in Melbourne. Our methods include steam cleaning, dry cleaning, and hand washing depending on fabric type. We remove dust, allergens, stains, and odors while preserving fabric quality. For blinds, we use specialized cleaning solutions that won't damage materials.",
        keywords: ["curtain cleaning Melbourne", "blind cleaning service", "drapes cleaning Melbourne"]
      }
    ],
    commercial: [
      {
        question: "What commercial cleaning services do you offer for Melbourne businesses?",
        answer: "We provide comprehensive commercial cleaning services for Melbourne offices, retail stores, medical facilities, schools, warehouses, and industrial spaces. Services include: daily office cleaning, floor maintenance, restroom sanitization, kitchenette cleaning, window cleaning, carpet maintenance, and specialized cleaning for healthcare and food service establishments.",
        keywords: ["commercial cleaning Melbourne", "office cleaning service", "business cleaning Melbourne"]
      },
      {
        question: "Can you provide after-hours cleaning for Melbourne businesses?",
        answer: "Yes, we specialize in after-hours cleaning for Melbourne businesses to minimize disruption to your operations. Our teams work evenings, nights, and weekends to clean offices, retail spaces, and commercial facilities. We provide secure access protocols and can work around your business schedule.",
        keywords: ["after hours cleaning Melbourne", "night cleaning service", "business after hours cleaning"]
      },
      {
        question: "Do you offer COVID-19 disinfection services for Melbourne workplaces?",
        answer: "Yes, we provide professional COVID-19 disinfection services using hospital-grade disinfectants and electrostatic sprayers. Our Melbourne commercial cleaning teams are trained in infection control protocols and can perform regular disinfection of high-touch surfaces, air purification, and complete facility sanitization.",
        keywords: ["COVID-19 cleaning Melbourne", "workplace disinfection", "commercial sanitization"]
      },
      {
        question: "What is included in your office cleaning service for Melbourne companies?",
        answer: "Our Melbourne office cleaning service includes: desk sanitization, trash removal, floor vacuuming and mopping, restroom cleaning and restocking, kitchenette cleaning, window cleaning, common area maintenance, and specialized cleaning for conference rooms and reception areas. We offer daily, weekly, or customized schedules.",
        keywords: ["office cleaning Melbourne CBD", "commercial floor cleaning", "workplace cleaning service"]
      },
      {
        question: "Do you provide cleaning contracts for Melbourne businesses?",
        answer: "Yes, we offer flexible cleaning contracts for Melbourne businesses with customizable terms. Contracts can include scheduled cleaning, emergency services, specialized cleaning needs, and performance guarantees. We work with businesses of all sizes from small offices to large corporate facilities.",
        keywords: ["cleaning contracts Melbourne", "commercial cleaning agreements", "business cleaning contracts"]
      }
    ],
    pricing: [
      {
        question: "How much does professional cleaning cost in Melbourne?",
        answer: "Cleaning costs in Melbourne vary based on property size, cleaning type, and frequency. Regular house cleaning starts from $120 for 2-3 bedroom homes. Deep cleaning ranges from $250-$500. Commercial cleaning quotes are customized based on square footage and specific requirements. We provide free, transparent quotes with no hidden fees.",
        keywords: ["cleaning prices Melbourne", "house cleaning cost", "affordable cleaning services"]
      },
      {
        question: "Do you offer discounts for recurring cleaning services in Melbourne?",
        answer: "Yes, we offer attractive discounts for recurring cleaning services: 10% off weekly cleaning, 15% off bi-weekly cleaning, and special rates for monthly maintenance cleaning. We also provide senior citizen discounts, military discounts, and package deals for multiple services booked together.",
        keywords: ["cleaning discounts Melbourne", "recurring service savings", "regular cleaning discounts"]
      },
      {
        question: "What payment methods do you accept for Melbourne cleaning services?",
        answer: "We accept all major payment methods: credit cards (Visa, MasterCard, American Express), bank transfers, and cash. For recurring commercial cleaning, we offer convenient invoicing with 7, 14, or 30-day payment terms. All transactions are secure and we provide detailed receipts.",
        keywords: ["cleaning payment options", "credit card cleaning payment", "secure payment methods"]
      },
      {
        question: "Are there any hidden fees in your Melbourne cleaning quotes?",
        answer: "No hidden fees - we believe in transparent pricing. Your quote includes all labor, equipment, and eco-friendly products. The only additional charges would be for extra services specifically requested beyond the agreed scope, which we always discuss and approve in advance.",
        keywords: ["transparent cleaning pricing", "no hidden fees cleaning", "honest cleaning quotes"]
      },
      {
        question: "Do you offer free quotes for cleaning services in Melbourne?",
        answer: "Yes, we provide free, no-obligation quotes for all cleaning services in Melbourne. We assess your specific needs, property size, and cleaning requirements to provide an accurate estimate. Quotes can be provided via phone, email, or through our online booking system.",
        keywords: ["free cleaning quote Melbourne", "no obligation estimate", "cleaning cost assessment"]
      },
      {
        question: "What factors affect the price of cleaning services in Melbourne?",
        answer: "Several factors influence cleaning prices: property size and layout, type of cleaning required, frequency of service, special requirements (pet hair, high ceilings), additional services (carpet cleaning, window washing), and travel distance. We consider all factors to provide fair, competitive pricing.",
        keywords: ["cleaning cost factors", "pricing considerations", "cleaning service variables"]
      }
    ],
    booking: [
      {
        question: "How do I book a cleaning service with Knight Cleanings in Melbourne?",
        answer: "Booking is easy: 1) Call +61 420 499 661 for immediate assistance, 2) Use our online booking system on our website, 3) Send a WhatsApp message for quick response, or 4) Email info@knightcleanings.com. We'll schedule a convenient time and provide a free quote within 24 hours.",
        keywords: ["book cleaning Melbourne", "cleaning appointment booking", "schedule cleaning service"]
      },
      {
        question: "How far in advance should I book cleaning services in Melbourne?",
        answer: "For regular cleaning, booking 48-72 hours in advance is recommended. For weekends, public holidays, or during peak seasons, book 5-7 days ahead. Emergency cleaning services are available with same-day response - call us anytime for urgent cleaning needs.",
        keywords: ["cleaning booking timeline", "advance booking cleaning", "last minute cleaning"]
      },
      {
        question: "What are your operating hours for Melbourne cleaning services?",
        answer: "We operate 7 days a week: Monday-Friday 7AM-9PM, Saturday 8AM-6PM, Sunday 9AM-5PM. Emergency cleaning services are available 24/7. Commercial cleaning can be scheduled outside business hours to minimize disruption to your operations.",
        keywords: ["cleaning service hours", "weekend cleaning available", "24/7 emergency cleaning"]
      },
      {
        question: "Can I schedule regular recurring cleaning in Melbourne?",
        answer: "Yes, we specialize in recurring cleaning schedules: weekly, bi-weekly, monthly, or customized frequencies. We offer discounted rates for recurring services and can set up automatic scheduling with flexible payment options. You can pause or adjust your schedule anytime.",
        keywords: ["recurring cleaning schedule", "regular maintenance cleaning", "subscription cleaning"]
      },
      {
        question: "Do you offer same-day cleaning services in Melbourne?",
        answer: "Yes, we provide same-day cleaning services for urgent needs. Availability depends on current bookings, so we recommend calling as early as possible. Same-day service is perfect for unexpected guests, emergency situations, or last-minute property inspections.",
        keywords: ["same day cleaning Melbourne", "emergency cleaning service", "urgent cleaning response"]
      }
    ],
    safety: [
      {
        question: "Are you insured for cleaning services in Melbourne?",
        answer: "Yes, Knight Cleanings is fully insured with $2 million public liability insurance and professional indemnity insurance. This covers any accidental damage to your property during our cleaning service. We're also bonded for your additional protection and provide certificates of currency upon request.",
        keywords: ["insured cleaners Melbourne", "cleaning insurance", "bonded cleaning service"]
      },
      {
        question: "What COVID-19 safety measures do your Melbourne cleaners follow?",
        answer: "Our COVID-19 safety protocols include: daily health checks for staff, mandatory mask wearing, gloves for all cleaning tasks, regular equipment sanitization, social distancing practices, hospital-grade disinfectants for high-touch surfaces, and contactless service options including digital payments and communication.",
        keywords: ["COVID safe cleaning", "pandemic cleaning protocols", "safe cleaning practices"]
      },
      {
        question: "How do you ensure quality control for cleaning services in Melbourne?",
        answer: "We implement strict quality control: team leaders supervise every cleaning, post-service inspections are conducted, customer satisfaction surveys follow each service, and we offer a 24-hour satisfaction guarantee. If you're not completely satisfied, we'll return to address any concerns at no additional cost.",
        keywords: ["cleaning quality assurance", "quality control cleaning", "satisfaction guaranteed cleaning"]
      },
      {
        question: "What happens if something gets damaged during cleaning in Melbourne?",
        answer: "In the rare event of damage, we immediately notify you and file a claim through our insurance. We take full responsibility and ensure quick resolution. Our comprehensive insurance covers accidental damage to property, and we maintain open communication throughout the resolution process.",
        keywords: ["damage protection cleaning", "insurance coverage cleaning", "property protection"]
      },
      {
        question: "Are your cleaning products safe for children and pets in Melbourne homes?",
        answer: "Absolutely. We use eco-friendly, non-toxic cleaning products certified safe for homes with children and pets. Our products are biodegradable, free from harsh chemicals, and leave no harmful residues. We can also accommodate specific product requests for sensitive individuals.",
        keywords: ["pet safe cleaning", "child friendly cleaning products", "non-toxic cleaning Melbourne"]
      }
    ],
    specialized: [
      {
        question: "Do you offer emergency flood restoration services in Melbourne?",
        answer: "Yes, we provide 24/7 emergency flood restoration services throughout Melbourne. Our rapid response team can be on-site within 60 minutes for water extraction, drying, dehumidification, mold prevention, and structural restoration. We work with insurance companies and provide detailed documentation.",
        keywords: ["flood restoration Melbourne", "water damage cleaning", "emergency flood cleanup"]
      },
      {
        question: "Can you clean air ducts and vents in Melbourne properties?",
        answer: "Yes, we specialize in professional air duct and vent cleaning services for Melbourne homes and businesses. Using specialized equipment, we remove dust, allergens, and contaminants from your HVAC system, improving indoor air quality and system efficiency. This service is particularly beneficial for allergy sufferers.",
        keywords: ["air duct cleaning Melbourne", "vent cleaning service", "HVAC cleaning Melbourne"]
      },
      {
        question: "Do you provide window and gutter cleaning services in Melbourne?",
        answer: "Yes, we offer comprehensive window and gutter cleaning services for Melbourne properties. This includes interior and exterior window cleaning, track cleaning, screen cleaning, and gutter clearing. We use professional equipment and safety gear for multi-story properties.",
        keywords: ["window cleaning Melbourne", "gutter cleaning service", "exterior cleaning Melbourne"]
      },
      {
        question: "Can you handle industrial and warehouse cleaning in Melbourne?",
        answer: "Yes, we provide industrial cleaning services for Melbourne warehouses, factories, and manufacturing facilities. Our services include: high dusting, floor scrubbing, equipment cleaning, waste removal, pressure washing, and specialized cleaning for industrial environments with heavy machinery.",
        keywords: ["industrial cleaning Melbourne", "warehouse cleaning service", "factory cleaning"]
      },
      {
        question: "Do you offer specialized cleaning for medical facilities in Melbourne?",
        answer: "Yes, we provide specialized cleaning services for Melbourne medical facilities, clinics, dental offices, and healthcare centers. Our teams are trained in infection control protocols, biohazard safety, medical waste handling, and maintaining sterile environments according to healthcare regulations.",
        keywords: ["medical cleaning Melbourne", "healthcare facility cleaning", "clinic cleaning service"]
      }
    ]
  };

  const stats = [
    { value: "15+", label: "Years Experience", icon: <Award className="text-blue-500" size={20} /> },
    { value: "10,000+", label: "Properties Cleaned", icon: <Home className="text-green-500" size={20} /> },
    { value: "98%", label: "Customer Satisfaction", icon: <CheckCircle className="text-amber-500" size={20} /> },
    { value: "24/7", label: "Emergency Service", icon: <Clock className="text-purple-500" size={20} /> },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header with Schema Markup */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <HelpCircle size={16} />
            FREQUENTLY ASKED QUESTIONS
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Melbourne Cleaning Services <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700">FAQs</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
            Get answers to common questions about professional cleaning services in Melbourne. 
            From residential cleaning to commercial maintenance, we've got you covered.
          </p>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-center gap-3 mb-2">
                  {stat.icon}
                  <div className="text-3xl font-bold text-gray-900">
                    {stat.value}
                  </div>
                </div>
                <div className="text-sm text-gray-600 text-center font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full transition-all duration-300 ${
                activeCategory === category.id
                  ? "bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg"
                  : "bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:shadow-md"
              }`}
            >
              {category.icon}
              <span className="font-semibold">{category.label}</span>
              <span className={`text-xs px-2 py-1 rounded-full ${
                activeCategory === category.id
                  ? "bg-white/20"
                  : "bg-blue-100 text-blue-700"
              }`}>
                {category.count}
              </span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* FAQ Accordions with Schema Structure */}
          <div className="lg:col-span-2" itemScope itemType="https://schema.org/FAQPage">
            <div className="space-y-4">
              {allFAQs[activeCategory].map((faq, index) => {
                const globalIndex = Object.keys(allFAQs)
                  .slice(0, categories.findIndex(c => c.id === activeCategory))
                  .reduce((acc, key) => acc + allFAQs[key].length, 0) + index;
                
                const isOpen = openFAQs.includes(globalIndex);

                return (
                  <div
                    key={globalIndex}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-blue-300 transition-all duration-300"
                    itemScope
                    itemProp="mainEntity"
                    itemType="https://schema.org/Question"
                  >
                    <button
                      onClick={() => toggleFAQ(globalIndex)}
                      className="w-full text-left p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      aria-expanded={isOpen}
                      aria-controls={`faq-answer-${globalIndex}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="bg-blue-50 p-2 rounded-lg">
                          <HelpCircle className="text-blue-600" size={20} />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1" itemProp="name">
                            {faq.question}
                          </h3>
                          <p className={`text-sm text-gray-500 transition-all duration-300 ${isOpen ? 'max-h-0 opacity-0' : 'max-h-6 opacity-100'}`}>
                            Click to {isOpen ? 'hide' : 'show'} answer
                          </p>
                        </div>
                      </div>
                      <ChevronDown
                        className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                        size={20}
                      />
                    </button>
                    
                    <div
                      id={`faq-answer-${globalIndex}`}
                      className={`transition-all duration-500 ease-in-out ${
                        isOpen
                          ? 'max-h-96 opacity-100'
                          : 'max-h-0 opacity-0'
                      }`}
                      itemScope
                      itemProp="acceptedAnswer"
                      itemType="https://schema.org/Answer"
                    >
                      <div className="px-6 pb-6 pl-16">
                        <div className="prose prose-blue max-w-none">
                          <p className="text-gray-600" itemProp="text">
                            {faq.answer}
                          </p>
                          {faq.keywords && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <p className="text-sm text-gray-500 mb-2">Related keywords:</p>
                              <div className="flex flex-wrap gap-2">
                                {faq.keywords.map((keyword, idx) => (
                                  <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                    {keyword}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* FAQ Actions */}
            <div className="flex justify-between items-center mt-8">
              <div className="text-sm text-gray-500">
                Showing {allFAQs[activeCategory].length} of {Object.values(allFAQs).flat().length} FAQs
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const allIndices = Object.values(allFAQs).flatMap((_, i) => 
                      [...Array(allFAQs[Object.keys(allFAQs)[i]].length).keys()].map(
                        (j) => Object.keys(allFAQs)
                          .slice(0, i)
                          .reduce((acc, key) => acc + allFAQs[key].length, 0) + j
                      )
                    );
                    setOpenFAQs(allIndices);
                  }}
                  className="text-blue-600 hover:text-blue-700 font-semibold px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  Expand All Answers
                </button>
                <button
                  onClick={() => setOpenFAQs([])}
                  className="text-gray-600 hover:text-gray-700 font-semibold px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Collapse All
                </button>
              </div>
            </div>
          </div>

          {/* Contact & Info Sidebar */}
          <div className="space-y-8">
            {/* Quick Contact Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-full mb-4">
                  <Users className="text-white" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Need Immediate Assistance?</h3>
                <p className="text-gray-600">Our Melbourne cleaning experts are available 7 days a week</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-white rounded-xl hover:shadow-md transition-shadow">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <Phone className="text-green-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Call Us Now</p>
                    <a href="tel:+61420499661" className="text-lg font-bold text-gray-900 hover:text-blue-600 transition-colors">
                      +61 420 499 661
                    </a>
                    <p className="text-xs text-gray-500 mt-1">24/7 emergency cleaning available</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-white rounded-xl hover:shadow-md transition-shadow">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <MessageSquare className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">WhatsApp Booking</p>
                    <a href="https://wa.me/61420499661" className="text-lg font-bold text-gray-900 hover:text-blue-600 transition-colors">
                      Chat on WhatsApp
                    </a>
                    <p className="text-xs text-gray-500 mt-1">Instant quotes and booking</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-white rounded-xl hover:shadow-md transition-shadow">
                  <div className="bg-amber-100 p-2 rounded-lg">
                    <Mail className="text-amber-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email Us</p>
                    <a href="mailto:info@knightcleanings.com" className="text-lg font-bold text-gray-900 hover:text-blue-600 transition-colors">
                      info@knightcleanings.com
                    </a>
                    <p className="text-xs text-gray-500 mt-1">Response within 2 hours</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Areas Card */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-green-100 p-2 rounded-lg">
                  <MapPin className="text-green-600" size={20} />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Service Areas in Melbourne</h3>
              </div>
              <div className="space-y-3">
                {[
                  "Melbourne CBD & Inner Suburbs",
                  "Eastern Suburbs (Glen Waverley, Mount Waverley)",
                  "South-Eastern Suburbs (Dandenong, Clayton, Noble Park)",
                  "Mornington Peninsula",
                  "All Surrounding Melbourne Areas"
                ].map((area, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <CheckCircle className="text-green-500" size={16} />
                    <span className="text-gray-700">{area}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-sm text-gray-600">
                  Not sure if we service your area? <a href="tel:+61420499661" className="text-blue-600 font-semibold hover:underline">Call us to check</a>
                </p>
              </div>
            </div>

            {/* CTA Card */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="text-amber-300" size={24} />
                <h3 className="text-2xl font-bold">Limited Time Offer</h3>
              </div>
              <p className="text-blue-100 mb-6">
                Get <span className="font-bold">20% OFF</span> your first cleaning service when you book online or via WhatsApp.
              </p>
              <div className="space-y-3">
                <a
                  href="https://wa.me/61420499661?text=Hi%20Knight%20Cleanings!%20I%27d%20like%20to%20claim%20the%2020%25%20discount%20on%20my%20first%20cleaning%20service."
                  className="block w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold py-3 px-6 rounded-full text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
                >
                  <MessageSquare size={18} />
                  Get Discount via WhatsApp
                </a>
                <Link
                  to="/booking"
                  className="block w-full bg-white text-blue-700 hover:bg-gray-100 font-bold py-3 px-6 rounded-full text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                >
                  Book Online
                </Link>
              </div>
              <p className="text-sm text-blue-200 text-center mt-4">
                Offer valid for new customers only
              </p>
            </div>

            {/* Trust Badges */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Why Choose Knight Cleanings</h4>
              <div className="space-y-3">
                {[
                  { icon: <ShieldCheck className="text-green-500" size={18} />, text: "Fully Insured & Bonded" },
                  { icon: <Leaf className="text-green-500" size={18} />, text: "Eco-Friendly Products" },
                  { icon: <Truck className="text-blue-500" size={18} />, text: "Same Day Service Available" },
                  { icon: <CheckCircle className="text-amber-500" size={18} />, text: "100% Satisfaction Guarantee" },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    {item.icon}
                    <span className="text-gray-700 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Additional Help Section */}
        <div className="mt-20 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Need Custom Cleaning Solutions in Melbourne?
              </h3>
              <p className="text-gray-600 text-lg mb-4">
                Have unique cleaning requirements, special requests, or need advice on the best cleaning approach for your property? Our Melbourne cleaning experts are here to help.
              </p>
              <div className="flex flex-wrap gap-4">
                {[
                  "Free On-Site Assessment",
                  "Customized Cleaning Plans",
                  "Emergency Response Teams",
                  "Specialized Equipment Available"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg">
                    <CheckCircle className="text-green-500" size={16} />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-end">
              <a
                href="https://wa.me/61420499661?text=Hi%20Knight%20Cleanings!%20I%20need%20custom%20cleaning%20solutions%20for%20my%20property%20in%20Melbourne."
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center justify-center gap-2"
              >
                <MessageSquare size={20} />
                WhatsApp for Custom Quote
              </a>
            </div>
          </div>
        </div>

        {/* Local SEO Keywords Section */}
        <div className="mt-12 bg-gray-50 rounded-2xl p-8">
          <h4 className="text-lg font-bold text-gray-900 mb-4 text-center">Melbourne Cleaning Services We Offer</h4>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              "End of Lease Cleaning Melbourne",
              "Carpet Cleaning Melbourne",
              "Office Cleaning Melbourne CBD",
              "Window Cleaning Melbourne",
              "Emergency Cleaning Melbourne",
              "Mattress Cleaning Melbourne",
              "Upholstery Cleaning Melbourne",
              "Commercial Cleaning Melbourne",
              "Industrial Cleaning Melbourne",
              "Air Duct Cleaning Melbourne",
              "Curtain Cleaning Melbourne",
              "Flood Restoration Melbourne"
            ].map((service, index) => (
              <span key={index} className="bg-white text-gray-700 px-4 py-2 rounded-full text-sm border border-gray-200 hover:border-blue-300 hover:text-blue-600 transition-colors">
                {service}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;